import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import {
  MultipleChoiceArtifact,
  ArtifactChoice,
  SpendAmount,
  DesiredActionKey,
  DesiredActionResponse,
  AnalyzeTodoClarifyingQuestion,
} from '@dreamai/types';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useRadiusCheck } from '../../../hooks/useRadiusCheck';
import FileLink from '../../shared/FileLink';
import { ReactComponent as TickSelectIcon } from '../../../icons/tick-select.svg';
import { ReactComponent as ChevronLeftIcon } from '../../../icons/chevron-left.svg';
import { ReactComponent as ChevronDownIcon } from '../../../icons/chevron-down.svg';
import { ReactComponent as SpeechBubbleIcon } from '../../../icons/speech-bubble.svg';
import { ReactComponent as NoLongerNeededIcon } from '../../../icons/no-longer-needed.svg';
import { ReactComponent as ActionBuyIcon } from '../../../icons/action-buy.svg';
import { ReactComponent as ActionResearchIcon } from '../../../icons/action-research.svg';

import { ReactComponent as ActionEmailSendIcon } from '../../../icons/action-email-send.svg';
import { ReactComponent as ActionBuyAndActIcon } from '../../../icons/action-buy-and-act.svg';
import { ReactComponent as ActionCalendarInviteIcon } from '../../../icons/action-calendar-invite.svg';
import { ReactComponent as ActionBringAndBuyIcon } from '../../../icons/action-bring-and-buy.svg';
import { ReactComponent as ActionDraftEmailIcon } from '../../../icons/action-draft-email.svg';
import { ReactComponent as ActionPhoneIcon } from '../../../icons/action-phone.svg';
import { ReactComponent as ActionPresentOptionsIcon } from '../../../icons/action-present-options.svg';
import { ReactComponent as ActionFillInIcon } from '../../../icons/action-fill-in.svg';
import { ReactComponent as ActionReserveIcon } from '../../../icons/action-reserve.svg';
import { ReactComponent as ActionActIcon } from '../../../icons/action-act.svg';
import { ReactComponent as ActionOtherIcon } from '../../../icons/action-other.svg';
import { ReactComponent as TransportItineraryIcon } from '../../../icons/transport-itinerary.svg';
import { ReactComponent as ActionProjectIcon } from '../../../icons/action-project.svg';
import { ReactComponent as MagicWandIcon } from '../../../icons/magic-wand.svg';
import { ReactComponent as EnhanceIcon } from '../../../icons/enhance.svg';
import { ReactComponent as FindMoreIcon } from '../../../icons/find-more.svg';
import { ReactComponent as ActionCreateIcon } from '../../../icons/action-create.svg';
import { ReactComponent as CloseXIcon } from '../../../icons/close-x.svg';
import { ReactComponent as MapIcon } from '../../../icons/map-pin-area.svg';
import {
  getActionLabel,
  getActionKeyLabel,
  ACTION_KEY_LABELS,
  SPENDING_ACTION_KEYS,
  getQuestionSpendApproval,
} from '../../../lib/desiredActionFields';
import ImageCard from '../../shared/ImageCard';
import MultipleChoiceMapView from './MultipleChoiceMapView';
import SpendingApprovalModal, { CardSelection } from './SpendingApprovalModal';
import { ReplyToItem } from '../../../contexts/ReplyToContext';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import './multiple-choice-full-screen.scss';

const ACTION_ICONS: Record<
  DesiredActionKey,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  buy: ActionBuyIcon,
  reserve: ActionReserveIcon,
  research: ActionResearchIcon,
  present_options: ActionPresentOptionsIcon,
  email_from_assistant: ActionEmailSendIcon,
  draft_email_from_user: ActionDraftEmailIcon,
  phone_from_assistant: ActionPhoneIcon,
  fill_in_by_assistant: ActionFillInIcon,
  send_calendar_invite: ActionCalendarInviteIcon,
  bring: TransportItineraryIcon,
  bring_and_buy: ActionBringAndBuyIcon,
  act: ActionActIcon,
  buy_and_act: ActionBuyAndActIcon,
  project: ActionProjectIcon,
  autopilot: MagicWandIcon,
  other: ActionOtherIcon,
  enhance: EnhanceIcon,
  find_more: FindMoreIcon,
  create: ActionCreateIcon,
};

interface SpendItem {
  id: string;
  actionLine: string;
  descriptionLine: string;
  price: number;
  price_currency: string;
  definitivePrice?: number;
  approxPriceRange?: [number, number];
  priceEstimate?: number;
  deliveryAllowance?: number;
  varianceAllowance?: number;
  varianceNeedReason?: string;
  purchaseSummary?: string;
  purchaseLink?: string;
  delivery?: string;
  deliveryAddress?: string;
}

/**
 * Parse a label that may contain a parenthesised action key like "(reserve)" or
 * "(email_from_assistant)" and render it with an inline icon + display label.
 */
function renderActionLabel(label: string, agentName?: string): React.ReactNode {
  const match = label.match(/\(([a-z_]+)\)/);
  if (!match) return label;
  const secondaryKey = match[1] as DesiredActionKey;
  const Icon = ACTION_ICONS[secondaryKey];
  if (!Icon) return label;
  const textBefore = label.slice(0, match.index).trimEnd();
  const textAfter = label.slice((match.index ?? 0) + match[0].length).trim();
  const displayLabel = getActionKeyLabel(secondaryKey, agentName);
  return (
    <>
      {textBefore}
      {textAfter ? ` ${textAfter}` : ''}{' '}
      <span className='mc-fs-secondary-action-badge'>
        (<Icon className='mc-fs-secondary-action-icon' />
        {displayLabel})
      </span>
    </>
  );
}

interface MultipleChoiceFullScreenProps {
  artifact: MultipleChoiceArtifact;
  selectedChoices: string[];
  onChoiceSelect: (choiceId: string) => void;
  onSubmitWithAction: (
    desiredAction: DesiredActionResponse,
    mandateItems?: Array<{
      actionLine: string;
      descriptionLine: string;
      price: number;
      price_currency: string;
      isDeposit?: boolean;
    }>,
    cardSelection?: CardSelection,
  ) => void;
  onSubmit: () => void;
  onAcknowledge: () => void;
  onClose: () => void;
  submitting: boolean;
  readOnly?: boolean;
  colorVariants?: {
    base: string;
    midDarker: string;
    darker: string;
    lighter: string;
  };
  pendingSpendItems: SpendItem[];
  isConfirmationLinked: boolean;
  onReject?: () => void;
  onApproveConfirmation?: () => void;
  onChatAbout?: () => void;
  chatButtonText: string;
  agentImageUrl?: string;
  agentName?: string;
  connectedEmails?: string[];
  onCheckSpendingIntent?: (
    message: string,
    context?: string,
  ) => Promise<{
    spendingIntent: boolean;
    spendType?: 'buy' | 'reserve';
    spendDescription?: string;
    estimatedAmount?: number;
    currency?: string;
  }>;
}

const FROM_EMAIL_FIELD_KEY = 'from_email_address';

const MultipleChoiceFullScreen: React.FC<MultipleChoiceFullScreenProps> = ({
  artifact,
  selectedChoices,
  onChoiceSelect,
  onSubmitWithAction,
  onSubmit,
  onAcknowledge,
  onClose,
  submitting,
  readOnly = false,
  colorVariants,
  pendingSpendItems,
  isConfirmationLinked,
  onReject,
  onApproveConfirmation,
  onChatAbout,
  chatButtonText,
  agentImageUrl,
  agentName,
  connectedEmails = [],
  onCheckSpendingIntent,
}) => {
  const { isMobile } = useMobileNav();
  const actionSectionRef = useRef<HTMLDivElement>(null);
  const prevSelectedCountRef = useRef(selectedChoices.length);
  const actionDropdownRef = useRef<HTMLDivElement>(null);
  const desktopDropdownRef = useRef<HTMLDivElement>(null);
  const mobileSheetRef = useRef<HTMLDivElement>(null);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideMobile = actionDropdownRef.current?.contains(target);
      const insideDesktop = desktopDropdownRef.current?.contains(target);
      const insideSheet = mobileSheetRef.current?.contains(target);
      if (!insideMobile && !insideDesktop && !insideSheet) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const STANDALONE_ACTION_KEYS = new Set(['enhance', 'find_more']);

  const desiredActions = useMemo(() => {
    const actions = (artifact.desiredActions || []).filter(
      (a) => !STANDALONE_ACTION_KEYS.has(a.key),
    );
    // Always ensure an "other" action exists
    if (!actions.some((a) => a.key === 'other')) {
      actions.push({
        key: 'other',
        label: 'What would you like me to do?',
      });
    }
    return [...actions].sort((a, b) => {
      if (a.key === 'other') return 1;
      if (b.key === 'other') return -1;
      return 0;
    });
  }, [artifact.desiredActions]);

  // Standalone action buttons (enhance / find_more) shown before item selection
  const [activeStandaloneAction, setActiveStandaloneAction] = useState<
    'enhance' | 'find_more' | null
  >(null);
  const [standaloneAnswers, setStandaloneAnswers] = useState<
    Record<string, string>
  >({});

  // Radius checking for address fields
  const {
    radiusChecking,
    radiusResults,
    checkRadiusForField,
    hasFieldOutsideRadius: checkFieldsOutsideRadius,
  } = useRadiusCheck();

  // Find the default action (agent recommendation) or fall back to first
  const defaultActionKey = useMemo(() => {
    const defaultAction = desiredActions.find((a) => a.default);
    return defaultAction?.key || desiredActions[0]?.key || null;
  }, [desiredActions]);

  // Action selection state
  const [selectedActionKey, setSelectedActionKey] = useState<string | null>(
    defaultActionKey,
  );
  const [questionAnswers, setQuestionAnswers] = useState<
    Record<string, string>
  >(() => {
    // Initialize from default action's clarifying question defaults
    const defaultAction = desiredActions.find(
      (a) => a.key === defaultActionKey,
    );
    const prefills: Record<string, string> = {};
    for (const q of defaultAction?.clarifyingQuestions || []) {
      if (q.defaultValue) prefills[q.fieldKey] = q.defaultValue;
    }
    return prefills;
  });
  const [otherText, setOtherText] = useState('');

  // Pre-fill answers when selected action changes
  const handleActionChange = useCallback(
    (key: string) => {
      setSelectedActionKey(key);
      const action = desiredActions.find((a) => a.key === key);
      const prefills: Record<string, string> = {};
      for (const q of action?.clarifyingQuestions || []) {
        if (q.defaultValue) prefills[q.fieldKey] = q.defaultValue;
      }
      setQuestionAnswers(prefills);
      setOtherText('');
    },
    [desiredActions],
  );

  // Get the currently selected action object
  const selectedAction = useMemo(
    () => desiredActions.find((a) => a.key === selectedActionKey) || null,
    [desiredActions, selectedActionKey],
  );

  // The questions for the currently selected action
  const questions: AnalyzeTodoClarifyingQuestion[] = useMemo(
    () => selectedAction?.clarifyingQuestions || [],
    [selectedAction],
  );

  // Whether any mustBeWithinRadius field is outside radius
  const fieldsOutsideRadius = useMemo(
    () => checkFieldsOutsideRadius(questions, questionAnswers),
    [checkFieldsOutsideRadius, questions, questionAnswers],
  );

  const [showSpendApproval, setShowSpendApproval] = useState(false);
  const [checkingSpendIntent, setCheckingSpendIntent] = useState(false);
  const otherSpendItemsRef = useRef<ReplyToItem[]>([]);

  // Determine if spend/deposit approval is needed from clarifying questions
  // (only when choices don't already have prices — choice prices take precedence)
  const questionSpendApproval = useMemo(() => {
    if (pendingSpendItems.length > 0) return null; // choice prices take precedence
    if (!selectedAction) return null;
    return getQuestionSpendApproval(selectedAction, questionAnswers);
  }, [selectedAction, pendingSpendItems, questionAnswers]);

  // Build spend approval items for SpendingApprovalModal (matches AddTodoModal pattern)
  const spendApprovalItems: ReplyToItem[] = useMemo(() => {
    // Choice-price items (when action is a spending action)
    if (
      pendingSpendItems.length > 0 &&
      selectedActionKey &&
      SPENDING_ACTION_KEYS.has(selectedActionKey as DesiredActionKey)
    ) {
      return pendingSpendItems.map((item) => ({
        id: item.id,
        actionLine: item.actionLine,
        descriptionLine: item.descriptionLine,
        bookable: true,
        price: item.price,
        price_currency: item.price_currency,
        purchaseLink: item.purchaseLink,
        delivery: item.delivery,
        deliveryAddress: item.deliveryAddress,
      }));
    }
    // Question-based spend (e.g. max_spend answers)
    if (questionSpendApproval && selectedAction) {
      return [
        {
          id: 'mc-spend',
          actionLine: selectedAction.label || selectedAction.key,
          descriptionLine: questionSpendApproval.description,
          bookable: true,
          price: questionSpendApproval.amount,
          price_currency: questionSpendApproval.currency,
          isDeposit: questionSpendApproval.isDeposit,
        },
      ];
    }
    return [];
  }, [
    pendingSpendItems,
    selectedActionKey,
    questionSpendApproval,
    selectedAction,
  ]);

  const spendIsDeposit = useMemo(() => {
    const items =
      spendApprovalItems.length > 0
        ? spendApprovalItems
        : otherSpendItemsRef.current;
    return items.some((item) => item.isDeposit);
  }, [spendApprovalItems, showSpendApproval]);

  const buildDesiredAction = useCallback((): DesiredActionResponse | null => {
    if (!selectedActionKey) return null;
    if (selectedActionKey === 'other') {
      return { key: 'other' as DesiredActionKey, customText: otherText };
    }
    return {
      key: selectedActionKey as DesiredActionKey,
      questionAnswers:
        Object.keys(questionAnswers).length > 0 ? questionAnswers : undefined,
      label: selectedAction?.label,
    };
  }, [selectedActionKey, otherText, questionAnswers, selectedAction]);

  const handleConfirm = useCallback(async () => {
    const action = buildDesiredAction();
    if (!action) return;

    // Gate on spending approval if there are spend items to approve
    if (spendApprovalItems.length > 0) {
      setShowSpendApproval(true);
      return;
    }

    // For "other" action, check if the free text implies spending
    if (action.key === 'other' && action.customText && onCheckSpendingIntent) {
      setCheckingSpendIntent(true);
      try {
        const context = artifact.title
          ? `${artifact.title}${artifact.overview ? `: ${artifact.overview}` : ''}`
          : undefined;
        const result = await onCheckSpendingIntent(action.customText, context);
        if (result.spendingIntent) {
          otherSpendItemsRef.current = [
            {
              id: 'other-spend',
              actionLine: result.spendType === 'reserve' ? 'Reserve' : 'Buy',
              descriptionLine: result.spendDescription || action.customText,
              bookable: true,
              price: result.estimatedAmount,
              price_currency: result.currency || 'GBP',
              isDeposit: result.spendType === 'reserve',
            },
          ];
          setShowSpendApproval(true);
          return;
        }
      } catch (err) {
        console.error('Error checking spending intent:', err);
      } finally {
        setCheckingSpendIntent(false);
      }
    }

    onSubmitWithAction(action);
  }, [
    buildDesiredAction,
    spendApprovalItems,
    onSubmitWithAction,
    onCheckSpendingIntent,
    artifact,
  ]);

  const cardSelectionRef = useRef<CardSelection | undefined>(undefined);

  const handleSpendApprove = useCallback(
    (cardSel?: CardSelection) => {
      cardSelectionRef.current = cardSel;
      const action = buildDesiredAction();
      if (!action) return;
      setShowSpendApproval(false);
      // Use otherSpendItemsRef if regular items are empty (from "other" action check)
      const itemsSource =
        spendApprovalItems.length > 0
          ? spendApprovalItems
          : otherSpendItemsRef.current;
      // Pass mandate items to the parent so the backend creates a mandate
      const mandateItems = itemsSource
        .filter(
          (item) => item.bookable && item.price != null && item.price_currency,
        )
        .map((item) => ({
          actionLine: item.actionLine,
          descriptionLine: item.descriptionLine,
          price: item.price!,
          price_currency: item.price_currency!,
          isDeposit: item.isDeposit,
        }));
      onSubmitWithAction(
        action,
        mandateItems.length > 0 ? mandateItems : undefined,
        cardSel,
      );
      otherSpendItemsRef.current = [];
    },
    [buildDesiredAction, spendApprovalItems, onSubmitWithAction],
  );

  const handleSpendCancel = useCallback(() => {
    setShowSpendApproval(false);
    otherSpendItemsRef.current = [];
  }, []);

  // Whether any required question is unanswered
  const hasUnansweredRequired = useMemo(() => {
    return questions.some(
      (q) => q.required && !questionAnswers[q.fieldKey]?.trim(),
    );
  }, [questions, questionAnswers]);

  // Determine if confirm button should be disabled
  const isConfirmDisabled =
    submitting ||
    checkingSpendIntent ||
    !selectedActionKey ||
    (selectedActionKey === 'other' && !otherText.trim()) ||
    hasUnansweredRequired ||
    fieldsOutsideRadius;

  // Get the confirm button text from the selected action label
  const confirmButtonText = useMemo(() => {
    if (!selectedAction) return 'Confirm';
    return getActionLabel(selectedAction);
  }, [selectedAction]);

  // Scroll to action section when first choice is selected
  useEffect(() => {
    const wasEmpty = prevSelectedCountRef.current === 0;
    const nowHasSelection = selectedChoices.length > 0;
    prevSelectedCountRef.current = selectedChoices.length;

    if (wasEmpty && nowHasSelection && actionSectionRef.current) {
      // Small delay to let the section render/animate in
      setTimeout(() => {
        actionSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [selectedChoices.length]);

  const formatPrice = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  };

  const formatChoicePrice = useCallback((item: SpendItem) => {
    if (item.definitivePrice != null) {
      return formatPrice(item.definitivePrice, item.price_currency);
    }
    if (item.approxPriceRange) {
      return `${formatPrice(item.approxPriceRange[0], item.price_currency)} – ${formatPrice(item.approxPriceRange[1], item.price_currency)}`;
    }
    if (item.priceEstimate != null) {
      return `~${formatPrice(item.priceEstimate, item.price_currency)}`;
    }
    return formatPrice(item.price, item.price_currency);
  }, []);

  const totalDeliveryAllowance = useMemo(
    () =>
      pendingSpendItems.reduce((sum, i) => sum + (i.deliveryAllowance || 0), 0),
    [pendingSpendItems],
  );

  const totalVarianceAllowance = useMemo(
    () =>
      pendingSpendItems.reduce((sum, i) => sum + (i.varianceAllowance || 0), 0),
    [pendingSpendItems],
  );

  const maxSpendDisplay = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const item of pendingSpendItems) {
      totals[item.price_currency] =
        (totals[item.price_currency] || 0) + item.price;
    }
    return Object.entries(totals)
      .map(([cur, amt]) => formatPrice(amt, cur))
      .join(' + ');
  }, [pendingSpendItems]);

  const renderChoiceText = (choice: ArtifactChoice) => (
    <div className='mc-fs-choice-text'>
      <b>{choice.choiceTitle}</b>
      {choice.choiceBlurb && (
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
          {choice.choiceBlurb}
        </ReactMarkdown>
      )}
      {choice.fields && choice.fields.length > 0 && (
        <div className='mc-fs-choice-fields'>
          {choice.fields.map((field, idx) => (
            <div key={idx} className='mc-fs-choice-field'>
              <span className='field-label'>{field.label}</span>
              <span className='field-value'>{field.value}</span>
            </div>
          ))}
        </div>
      )}
      {choice.links && choice.links.length > 0 && (
        <div className='mc-fs-choice-links'>
          {choice.links.map((link, idx) => (
            <FileLink
              key={idx}
              url={link.url}
              onClick={(e) => e.stopPropagation()}
            >
              {link.label}
            </FileLink>
          ))}
        </div>
      )}
    </div>
  );

  const standaloneQuestions: {
    key: 'enhance' | 'find_more';
    label: string;
    subtitle: string;
    fields: Array<{
      fieldKey: string;
      label: string;
      type: 'text' | 'textarea';
      required: boolean;
    }>;
  }[] = [
    {
      key: 'enhance',
      label: 'Enhance',
      subtitle: 'I will enhance the information',
      fields: [
        {
          fieldKey: 'additional_info',
          label: 'What new information would you like?',
          type: 'textarea',
          required: true,
        },
      ],
    },
    {
      key: 'find_more',
      label: 'Find More',
      subtitle: 'I will find more options',
      fields: [
        {
          fieldKey: 'how_many_more',
          label: 'How many more?',
          type: 'text',
          required: false,
        },
        {
          fieldKey: 'additional_guidance',
          label: 'Any additional guidance?',
          type: 'textarea',
          required: false,
        },
      ],
    },
  ];

  const handleStandaloneToggle = useCallback(
    (key: 'enhance' | 'find_more') => {
      if (activeStandaloneAction === key) {
        setActiveStandaloneAction(null);
        setStandaloneAnswers({});
      } else {
        setActiveStandaloneAction(key);
        setStandaloneAnswers({});
      }
    },
    [activeStandaloneAction],
  );

  const handleStandaloneSubmit = useCallback(() => {
    if (!activeStandaloneAction) return;
    const action: DesiredActionResponse = {
      key: activeStandaloneAction as DesiredActionKey,
      questionAnswers:
        Object.keys(standaloneAnswers).length > 0
          ? standaloneAnswers
          : undefined,
    };
    onSubmitWithAction(action);
    setActiveStandaloneAction(null);
    setStandaloneAnswers({});
  }, [activeStandaloneAction, standaloneAnswers, onSubmitWithAction]);

  const isStandaloneSubmitDisabled = useMemo(() => {
    if (!activeStandaloneAction) return true;
    const config = standaloneQuestions.find(
      (q) => q.key === activeStandaloneAction,
    );
    if (!config) return true;
    return config.fields.some(
      (f) => f.required && !standaloneAnswers[f.fieldKey]?.trim(),
    );
  }, [activeStandaloneAction, standaloneAnswers]);

  const hasSelection = selectedChoices.length > 0;

  // Reset expanded sidebar when selection is cleared
  useEffect(() => {
    if (!hasSelection) setSidebarExpanded(false);
  }, [hasSelection]);

  return (
    <div
      className={`mc-full-screen${sidebarExpanded ? ' mc-fs-expanded' : ''}`}
    >
      {sidebarExpanded && (
        <div
          className='mc-fs-backdrop'
          onClick={() => setSidebarExpanded(false)}
        />
      )}
      <div className='mc-fs-scroll-area'>
        {/* Back button */}
        <div className='mc-fs-top-bar'>
          <button className='mc-fs-back-btn' onClick={onClose}>
            <ChevronLeftIcon />
            <span>Back to Todo list</span>
          </button>
        </div>

        {/* Breakout hero header */}
        <div className='mc-fs-hero'>
          <h1 className='mc-fs-title'>{artifact.title || 'Action required'}</h1>
          {!readOnly && (
            <button
              className='mc-fs-remove-btn mc-fs-mobile-only'
              onClick={onAcknowledge}
              disabled={submitting}
            >
              <NoLongerNeededIcon /> Remove task
            </button>
          )}
        </div>

        {/* Content column */}
        <div className='mc-fs-content'>
          {artifact.overview && (
            <div className='mc-fs-overview'>
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                {artifact.overview}
              </ReactMarkdown>
            </div>
          )}

          {/* Two-column layout: choices left, action sidebar right */}
          <div
            className={`mc-fs-columns${artifact.view === 'map' ? ' mc-fs-columns-map' : ''}`}
          >
            {/* Left column: choice cards (or map view) */}
            {artifact.view === 'map' ? (
              <>
                {/* Desktop: map area (hidden on mobile via CSS) */}
                <div className='mc-fs-map-area'>
                  <MultipleChoiceMapView
                    choices={artifact.choices}
                    selectedChoices={selectedChoices}
                    onChoiceSelect={onChoiceSelect}
                    readOnly={readOnly}
                  />
                </div>
                {/* Mobile: list view with map button (hidden on desktop via CSS) */}
                <div className='mc-fs-mobile-map-list'>
                  <button
                    className='mc-fs-show-map-btn'
                    onClick={() => setMobileMapOpen(true)}
                  >
                    <MapIcon />
                    <span>Show results on map</span>
                  </button>
                  <div className='mc-fs-choices'>
                    {artifact.choices.map((choice) => (
                      <div
                        key={choice.choiceId}
                        className={`mc-fs-choice-item ${selectedChoices.includes(choice.choiceId) ? 'selected' : ''} ${readOnly ? 'read-only' : ''} ${choice.imageHeavy ? 'image-heavy' : ''}`}
                        onClick={() =>
                          !choice.imageHeavy &&
                          !readOnly &&
                          onChoiceSelect(choice.choiceId)
                        }
                        role='button'
                        tabIndex={0}
                      >
                        {choice.imageHeavy ? (
                          <ImageCard
                            id={choice.choiceId}
                            title={choice.choiceTitle}
                            blurb={choice.choiceBlurb}
                            imageUrl={choice.imageUrl}
                            additionalImages={choice.additionalImages}
                            fields={choice.fields}
                            links={choice.links}
                            selected={selectedChoices.includes(choice.choiceId)}
                            onSelect={() => onChoiceSelect(choice.choiceId)}
                            readOnly={readOnly}
                            accentColor={colorVariants?.darker}
                          />
                        ) : (
                          <>
                            <div
                              className={`mc-fs-choice-tick ${selectedChoices.includes(choice.choiceId) ? 'selected' : ''}`}
                            >
                              <TickSelectIcon />
                            </div>
                            {choice.imageUrl && (
                              <div className='mc-fs-choice-image'>
                                <img
                                  src={choice.imageUrl}
                                  alt={choice.choiceTitle}
                                  onError={(e) => {
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                            {renderChoiceText(choice)}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className='mc-fs-choices'>
                {artifact.choices.map((choice) => (
                  <div
                    key={choice.choiceId}
                    className={`mc-fs-choice-item ${selectedChoices.includes(choice.choiceId) ? 'selected' : ''} ${readOnly ? 'read-only' : ''} ${choice.imageHeavy ? 'image-heavy' : ''}`}
                    onClick={() =>
                      !choice.imageHeavy &&
                      !readOnly &&
                      onChoiceSelect(choice.choiceId)
                    }
                    role='button'
                    tabIndex={0}
                  >
                    {choice.imageHeavy ? (
                      <ImageCard
                        id={choice.choiceId}
                        title={choice.choiceTitle}
                        blurb={choice.choiceBlurb}
                        imageUrl={choice.imageUrl}
                        additionalImages={choice.additionalImages}
                        fields={choice.fields}
                        links={choice.links}
                        selected={selectedChoices.includes(choice.choiceId)}
                        onSelect={() => onChoiceSelect(choice.choiceId)}
                        readOnly={readOnly}
                        accentColor={colorVariants?.darker}
                      />
                    ) : (
                      <>
                        <div
                          className={`mc-fs-choice-tick ${selectedChoices.includes(choice.choiceId) ? 'selected' : ''}`}
                        >
                          <TickSelectIcon />
                        </div>
                        {choice.imageUrl && (
                          <div className='mc-fs-choice-image'>
                            <img
                              src={choice.imageUrl}
                              alt={choice.choiceTitle}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  'none';
                              }}
                            />
                          </div>
                        )}
                        {renderChoiceText(choice)}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Right column: sticky action sidebar */}
            {!readOnly && (
              <div
                className={`mc-fs-sidebar${sidebarExpanded ? ' expanded' : ''}`}
                ref={actionSectionRef}
              >
                <div
                  className={`mc-fs-sidebar-inner ${hasSelection ? 'has-selection' : ''}`}
                >
                  {hasSelection && desiredActions.length > 0 && (
                    <div className='mc-fs-next-steps-area'>
                      {/* ── Mobile: compact nextstep bar ── */}
                      <div className='mc-fs-nextstep-bar'>
                        <div className='mc-fs-nextstep-bar-header'>
                          <span className='mc-fs-nextstep-label'>
                            I want to
                          </span>
                          <span className='mc-fs-nextstep-count'>
                            {selectedChoices.length} option
                            {selectedChoices.length !== 1 ? 's' : ''} selected
                          </span>
                        </div>
                        <div className='mc-fs-nextstep-bar-row'>
                          <div
                            className='mc-fs-dropdown-wrapper'
                            ref={actionDropdownRef}
                          >
                            <button
                              className={`mc-fs-action-btn-trigger${actionDropdownOpen ? ' active' : ''}`}
                              onClick={() => {
                                setActionDropdownOpen(!actionDropdownOpen);
                                if (!sidebarExpanded) setSidebarExpanded(true);
                              }}
                              disabled={submitting}
                            >
                              {selectedAction &&
                                ACTION_ICONS[selectedAction.key] &&
                                React.createElement(
                                  ACTION_ICONS[selectedAction.key],
                                )}
                              {selectedAction
                                ? getActionKeyLabel(
                                    selectedAction.key,
                                    agentName,
                                  )
                                : 'Select action'}
                              <ChevronDownIcon className='chevron-icon' />
                            </button>
                            {actionDropdownOpen && !isMobile && (
                              <div className='mc-fs-dropdown-menu'>
                                {desiredActions.map((action) => (
                                  <button
                                    key={action.key}
                                    className={`mc-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                    onClick={() => {
                                      handleActionChange(action.key);
                                      setActionDropdownOpen(false);
                                    }}
                                  >
                                    {ACTION_ICONS[action.key] &&
                                      React.createElement(
                                        ACTION_ICONS[action.key],
                                      )}
                                    <div className='mc-fs-dropdown-item-text'>
                                      <span className='mc-fs-dropdown-item-title'>
                                        {getActionKeyLabel(
                                          action.key,
                                          agentName,
                                        )}
                                      </span>
                                      <span className='mc-fs-dropdown-item-subtitle'>
                                        {renderActionLabel(
                                          getActionLabel(action),
                                          agentName,
                                        )}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            {actionDropdownOpen &&
                              isMobile &&
                              createPortal(
                                <div
                                  className='mc-fs-dropdown-sheet-backdrop'
                                  onClick={() => setActionDropdownOpen(false)}
                                >
                                  <div
                                    className='mc-fs-dropdown-sheet'
                                    ref={mobileSheetRef}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className='mc-fs-dropdown-sheet-header'>
                                      What would you like to do?
                                    </div>
                                    {desiredActions.map((action) => (
                                      <button
                                        key={action.key}
                                        className={`mc-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                        onClick={() => {
                                          handleActionChange(action.key);
                                          setActionDropdownOpen(false);
                                        }}
                                      >
                                        {ACTION_ICONS[action.key] &&
                                          React.createElement(
                                            ACTION_ICONS[action.key],
                                          )}
                                        <div className='mc-fs-dropdown-item-text'>
                                          <span className='mc-fs-dropdown-item-title'>
                                            {getActionKeyLabel(
                                              action.key,
                                              agentName,
                                            )}
                                          </span>
                                          <span className='mc-fs-dropdown-item-subtitle'>
                                            {renderActionLabel(
                                              getActionLabel(action),
                                              agentName,
                                            )}
                                          </span>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>,
                                document.body,
                              )}
                          </div>
                          {!sidebarExpanded ? (
                            <button
                              className='mc-fs-next-btn'
                              onClick={() => setSidebarExpanded(true)}
                            >
                              Next
                            </button>
                          ) : (
                            <button
                              className='mc-fs-close-sidebar-btn'
                              onClick={() => setSidebarExpanded(false)}
                            >
                              <CloseXIcon />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* ── Desktop: Next steps header + dropdown ── */}
                      <div className='mc-fs-section mc-fs-nextsteps mc-fs-desktop-only'>
                        <div className='mc-fs-section-title'>Next steps</div>
                        <div
                          className='mc-fs-dropdown-wrapper'
                          ref={desktopDropdownRef}
                        >
                          <button
                            className={`mc-fs-action-btn-trigger${actionDropdownOpen ? ' active' : ''}`}
                            onClick={() =>
                              setActionDropdownOpen(!actionDropdownOpen)
                            }
                            disabled={submitting}
                          >
                            {selectedAction &&
                              ACTION_ICONS[selectedAction.key] &&
                              React.createElement(
                                ACTION_ICONS[selectedAction.key],
                              )}
                            {selectedAction
                              ? getActionKeyLabel(selectedAction.key, agentName)
                              : 'Select action'}
                            <ChevronDownIcon className='chevron-icon' />
                          </button>
                          {actionDropdownOpen && (
                            <div className='mc-fs-dropdown-menu'>
                              {desiredActions.map((action) => (
                                <button
                                  key={action.key}
                                  className={`mc-fs-dropdown-item${action.key === selectedActionKey ? ' selected' : ''}`}
                                  onClick={() => {
                                    handleActionChange(action.key);
                                    setActionDropdownOpen(false);
                                  }}
                                >
                                  {ACTION_ICONS[action.key] &&
                                    React.createElement(
                                      ACTION_ICONS[action.key],
                                    )}
                                  <div className='mc-fs-dropdown-item-text'>
                                    <span className='mc-fs-dropdown-item-title'>
                                      {getActionKeyLabel(action.key, agentName)}
                                    </span>
                                    <span className='mc-fs-dropdown-item-subtitle'>
                                      {renderActionLabel(
                                        getActionLabel(action),
                                        agentName,
                                      )}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ── Detail sections (desktop: always visible, mobile: only when expanded) ── */}
                      <div className='mc-fs-next-steps-detail'>
                        {/* ── Section: Selected items ── */}
                        <div className='mc-fs-selected-items'>
                          {pendingSpendItems.length > 0 &&
                          selectedActionKey &&
                          SPENDING_ACTION_KEYS.has(
                            selectedActionKey as DesiredActionKey,
                          )
                            ? pendingSpendItems.map((item) => (
                                <div
                                  key={item.id}
                                  className='mc-fs-selected-item'
                                >
                                  <span className='mc-fs-selected-item-price'>
                                    {formatChoicePrice(item)}
                                  </span>
                                  <p>{item.descriptionLine}</p>
                                </div>
                              ))
                            : selectedChoices.map((choiceId) => {
                                const choice = artifact.choices.find(
                                  (c) => c.choiceId === choiceId,
                                );
                                if (!choice) return null;
                                return (
                                  <div
                                    key={choiceId}
                                    className='mc-fs-selected-item'
                                  >
                                    <p>{choice.choiceTitle}</p>
                                  </div>
                                );
                              })}
                        </div>

                        {/* ── Section: Spending summary (conditional) ── */}
                        {pendingSpendItems.length > 0 &&
                          selectedActionKey &&
                          SPENDING_ACTION_KEYS.has(
                            selectedActionKey as DesiredActionKey,
                          ) && (
                            <div className='mc-fs-spend-summary'>
                              {totalDeliveryAllowance > 0 && (
                                <div className='mc-fs-spend-extra-line'>
                                  <span className='mc-fs-spend-extra-amount'>
                                    {formatPrice(
                                      totalDeliveryAllowance,
                                      pendingSpendItems[0]?.price_currency ||
                                        'GBP',
                                    )}
                                  </span>
                                  <span className='mc-fs-spend-extra-label'>
                                    Likely delivery cost*
                                  </span>
                                </div>
                              )}
                              {totalVarianceAllowance > 0 && (
                                <div className='mc-fs-spend-extra-line'>
                                  <span className='mc-fs-spend-extra-amount'>
                                    {formatPrice(
                                      totalVarianceAllowance,
                                      pendingSpendItems[0]?.price_currency ||
                                        'GBP',
                                    )}
                                  </span>
                                  <span className='mc-fs-spend-extra-label'>
                                    Possible additional costs*
                                  </span>
                                </div>
                              )}
                              <div className='mc-fs-spend-label'>Max spend</div>
                              <div className='mc-fs-spend-amount'>
                                {maxSpendDisplay}
                              </div>
                              {(totalDeliveryAllowance > 0 ||
                                totalVarianceAllowance > 0) && (
                                <div className='mc-fs-spend-footnotes'>
                                  {totalDeliveryAllowance > 0 && (
                                    <p>
                                      * Delivery cost is estimated and included
                                      in the max spend to cover shipping or
                                      transport.
                                    </p>
                                  )}
                                  {totalVarianceAllowance > 0 && (
                                    <p>
                                      *{' '}
                                      {pendingSpendItems.find(
                                        (i) => i.varianceNeedReason,
                                      )?.varianceNeedReason ||
                                        'Additional headroom included for possible price fluctuations.'}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        {!pendingSpendItems.length && questionSpendApproval && (
                          <div className='mc-fs-spend-summary'>
                            <div className='mc-fs-spend-label'>
                              {questionSpendApproval.isDeposit
                                ? 'Max deposit'
                                : 'Max spend'}
                            </div>
                            <div className='mc-fs-spend-amount'>
                              {formatPrice(
                                questionSpendApproval.amount,
                                questionSpendApproval.currency,
                              )}
                            </div>
                          </div>
                        )}

                        {selectedActionKey !== 'other' &&
                          questions.length > 0 && (
                            <div className='mc-fs-questions'>
                              {questions.map((q) => {
                                const isCheckingRadius =
                                  radiusChecking[q.fieldKey];
                                const isOutsideRadius =
                                  q.mustBeWithinRadius &&
                                  questionAnswers[q.fieldKey] &&
                                  radiusResults[q.fieldKey] === false;

                                return (
                                  <div
                                    key={q.fieldKey}
                                    className={`mc-fs-question${isOutsideRadius ? ' outside-radius' : ''}`}
                                  >
                                    <label>
                                      {q.label}
                                      {q.required && (
                                        <span className='mc-fs-required'>
                                          *
                                        </span>
                                      )}
                                    </label>
                                    {q.type === 'select' ? (
                                      <select
                                        value={
                                          questionAnswers[q.fieldKey] || ''
                                        }
                                        onChange={(e) =>
                                          setQuestionAnswers((prev) => ({
                                            ...prev,
                                            [q.fieldKey]: e.target.value,
                                          }))
                                        }
                                      >
                                        <option value=''>Select...</option>
                                        {(q.fieldKey === FROM_EMAIL_FIELD_KEY &&
                                        q.type === 'select' &&
                                        connectedEmails.length > 0
                                          ? connectedEmails
                                          : q.options || []
                                        ).map((opt) => (
                                          <option key={opt} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <div className='mc-fs-input-wrapper'>
                                        {q.type === 'number' && q.currency && (
                                          <span className='mc-fs-currency-prefix'>
                                            {getSymbolFromCurrency(
                                              q.currency,
                                            ) || q.currency}
                                          </span>
                                        )}
                                        {q.type === 'textarea' ? (
                                          <textarea
                                            className='mc-fs-textarea'
                                            value={
                                              questionAnswers[q.fieldKey] || ''
                                            }
                                            onChange={(e) =>
                                              setQuestionAnswers((prev) => ({
                                                ...prev,
                                                [q.fieldKey]: e.target.value,
                                              }))
                                            }
                                            placeholder=''
                                            rows={3}
                                          />
                                        ) : (
                                          <input
                                            type={
                                              q.type === 'number'
                                                ? 'number'
                                                : 'text'
                                            }
                                            value={
                                              questionAnswers[q.fieldKey] || ''
                                            }
                                            onChange={(e) =>
                                              setQuestionAnswers((prev) => ({
                                                ...prev,
                                                [q.fieldKey]: e.target.value,
                                              }))
                                            }
                                            onBlur={() => {
                                              if (
                                                q.mustBeWithinRadius &&
                                                questionAnswers[q.fieldKey]
                                              ) {
                                                checkRadiusForField(
                                                  q.fieldKey,
                                                  questionAnswers[q.fieldKey],
                                                );
                                              }
                                            }}
                                            placeholder=''
                                          />
                                        )}
                                      </div>
                                    )}
                                    {isCheckingRadius && (
                                      <span className='mc-fs-field-hint checking'>
                                        Checking area...
                                      </span>
                                    )}
                                    {isOutsideRadius && (
                                      <span className='mc-fs-field-hint error'>
                                        Outside Dreamster operating area
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                        {selectedActionKey === 'other' && (
                          <div className='mc-fs-questions'>
                            <div className='mc-fs-question'>
                              <label>What would you like me to do?</label>
                              <textarea
                                className='mc-fs-textarea'
                                value={otherText}
                                onChange={(e) => setOtherText(e.target.value)}
                                placeholder=''
                                rows={3}
                              />
                            </div>
                          </div>
                        )}

                        {/* ── Confirm / Reject buttons ── */}
                        {!showSpendApproval && (
                          <div className='mc-fs-confirm-wrapper'>
                            <button
                              className='mc-fs-action-btn'
                              onClick={handleConfirm}
                              disabled={isConfirmDisabled}
                            >
                              {checkingSpendIntent ? 'Checking...' : 'Confirm'}
                            </button>
                          </div>
                        )}
                      </div>
                      {/* end mc-fs-next-steps-detail */}
                    </div>
                  )}

                  <SpendingApprovalModal
                    show={showSpendApproval}
                    onApprove={handleSpendApprove}
                    onCancel={handleSpendCancel}
                    items={
                      spendApprovalItems.length > 0
                        ? spendApprovalItems
                        : otherSpendItemsRef.current
                    }
                    agentImageUrl={agentImageUrl}
                    assistantName={agentName || 'Assistant'}
                    isDeposit={spendIsDeposit}
                    merchantName={selectedAction?.merchantName ?? undefined}
                  />

                  {hasSelection && onChatAbout && (
                    <button className='mc-fs-chat-btn' onClick={onChatAbout}>
                      <SpeechBubbleIcon />
                      {chatButtonText}
                    </button>
                  )}

                  {!hasSelection && (
                    <>
                      <div className='mc-fs-standalone-actions mc-fs-standalone-actions-sidebar'>
                        <div className='mc-fs-standalone-header'>
                          {standaloneQuestions.map((sq) => {
                            const isActive = activeStandaloneAction === sq.key;
                            const Icon = ACTION_ICONS[sq.key];
                            return (
                              <button
                                key={sq.key}
                                className={`mc-fs-standalone-btn-trigger${isActive ? ' active' : ''}`}
                                onClick={() => handleStandaloneToggle(sq.key)}
                                disabled={submitting}
                              >
                                {Icon && <Icon />}
                                <span className='mc-fs-standalone-btn-text'>
                                  <p className='mc-fs-standalone-btn-label'>
                                    {sq.label}
                                  </p>
                                  <p className='mc-fs-standalone-btn-subtitle'>
                                    {sq.subtitle}
                                  </p>
                                </span>
                                {isActive && (
                                  <span className='mc-fs-standalone-btn-close'>
                                    <CloseXIcon />
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {activeStandaloneAction &&
                          (() => {
                            const activeSq = standaloneQuestions.find(
                              (sq) => sq.key === activeStandaloneAction,
                            );
                            if (!activeSq) return null;
                            return (
                              <div className='mc-fs-standalone-lower'>
                                {activeSq.fields.map((f) => (
                                  <div
                                    key={f.fieldKey}
                                    className='mc-fs-question'
                                  >
                                    <label>
                                      {f.label}
                                      {f.required && (
                                        <span className='mc-fs-required'>
                                          *
                                        </span>
                                      )}
                                    </label>
                                    {f.type === 'textarea' ? (
                                      <textarea
                                        className='mc-fs-textarea'
                                        value={
                                          standaloneAnswers[f.fieldKey] || ''
                                        }
                                        onChange={(e) =>
                                          setStandaloneAnswers((prev) => ({
                                            ...prev,
                                            [f.fieldKey]: e.target.value,
                                          }))
                                        }
                                        placeholder=''
                                        rows={3}
                                      />
                                    ) : (
                                      <input
                                        type='text'
                                        value={
                                          standaloneAnswers[f.fieldKey] || ''
                                        }
                                        onChange={(e) =>
                                          setStandaloneAnswers((prev) => ({
                                            ...prev,
                                            [f.fieldKey]: e.target.value,
                                          }))
                                        }
                                        placeholder=''
                                      />
                                    )}
                                  </div>
                                ))}
                                <button
                                  className='mc-fs-standalone-submit'
                                  onClick={handleStandaloneSubmit}
                                  disabled={
                                    isStandaloneSubmitDisabled || submitting
                                  }
                                >
                                  Confirm
                                </button>
                              </div>
                            );
                          })()}
                      </div>
                      <button
                        className='mc-fs-remove-btn mc-fs-desktop-only'
                        onClick={onAcknowledge}
                        disabled={submitting}
                      >
                        <NoLongerNeededIcon /> Remove task
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile fullscreen map modal */}
      {mobileMapOpen && artifact.view === 'map' && (
        <div className='mc-mobile-map-modal'>
          <button
            className='mc-mobile-map-close'
            onClick={() => setMobileMapOpen(false)}
          >
            <CloseXIcon />
          </button>
          <MultipleChoiceMapView
            choices={artifact.choices}
            selectedChoices={selectedChoices}
            onChoiceSelect={onChoiceSelect}
            readOnly={readOnly}
          />
        </div>
      )}
    </div>
  );
};

export default MultipleChoiceFullScreen;
